import { useMemo, useCallback } from 'react'
import {
  Autocomplete,
  Caption,
  type AutocompleteProps,
} from '@design-systems/canvas-ui'
import { debounce } from 'lodash'
import { getUserDisplayName } from 'users/utils/userDisplayHelpers'
import { useLazyQuery } from '@apollo/client/react'
import { SEARCH_USERS_AUTOCOMPLETE } from 'graphql/queries/users/queries/userQueries'

// Canvas-ui doesn't export the RenderOption arg type, so derive it from
// the `renderOption` prop on AutocompleteProps.
type RenderOption = Parameters<
  NonNullable<AutocompleteProps['renderOption']>
>[0]

const DEBOUNCE_MS = 300

export type UserOption = {
  value: string
  label: string
  id: string
  data: {
    id: string
    firstName?: string
    lastName?: string
    email?: string
    companyName?: string
    internal?: boolean
    lanId?: string
  }
}

export type UserSearchScope =
  | { kind: 'targetOnly' }
  | { kind: 'targetAndPartner'; partnerId: number }
  | { kind: 'all' }

export const getBaseMatcher = (searchQuery: string) =>
  `(firstName:${searchQuery}* OR lastName:${searchQuery}* OR email:${searchQuery}* OR userProfileId:${searchQuery}*)`

const buildQueryString = (
  scope: UserSearchScope,
  searchQuery: string,
): string => {
  const matcher = getBaseMatcher(searchQuery)
  switch (scope.kind) {
    case 'targetOnly':
      return `internal:true AND ${matcher}`
    case 'targetAndPartner':
      return `(internal:true OR partnerId:${scope.partnerId}) AND ${matcher}`
    case 'all':
      return matcher
  }
}

type UserSearchAutocompleteProps = {
  scope: UserSearchScope
  onUserSelect: (user: UserOption) => void
  value?: UserOption | null
}

type CanvasAutocompleteProps = Omit<
  React.ComponentProps<typeof Autocomplete>,
  'options' | 'onChange' | 'value' | 'renderOption'
>

const UserSearchAutocomplete = ({
  onUserSelect,
  value,
  scope,
  ...canvasProps
}: UserSearchAutocompleteProps & CanvasAutocompleteProps) => {
  const [fetchUsers] = useLazyQuery(SEARCH_USERS_AUTOCOMPLETE, {
    fetchPolicy: 'no-cache',
  })

  const debouncedFetch = useMemo(
    () =>
      debounce(
        async (query: string, resolve: (options: UserOption[]) => void) => {
          const queryString = buildQueryString(scope, query)

          try {
            const { data } = await fetchUsers({
              variables: { q: queryString },
            })
            const results = data?.searchUsers?.content || []
            resolve(
              results.map((item) => ({
                value: item.id,
                label: getUserDisplayName(item.firstName, item.lastName),
                id: item.id,
                data: item,
              })),
            )
          } catch (error) {
            console.error('Error fetching users suggestions:', error)
            resolve([])
          }
        },
        DEBOUNCE_MS,
      ),
    [scope, fetchUsers],
  )

  const handleUserSearchChange = useCallback(
    (searchText: string) =>
      new Promise<UserOption[]>((resolve) => {
        const sanitized = searchText?.trim() || ''
        const query = sanitized.length < 3 ? '*' : sanitized
        debouncedFetch(query, resolve)
      }),
    [debouncedFetch],
  )

  const handleUserSelect = useCallback(
    (option: any) => {
      onUserSelect?.(option)
    },
    [onUserSelect],
  )

  const renderOption = useCallback(({ option }: RenderOption) => {
    const userOption = option as UserOption
    return (
      <Caption
        below={`${userOption.data.companyName || 'Target'} - ${userOption.data.email}`}
      >
        {userOption.label}
      </Caption>
    )
  }, [])

  return (
    <Autocomplete
      value={value?.value}
      placeholder="Search name, email, or ID"
      options={handleUserSearchChange}
      renderOption={renderOption}
      onChange={handleUserSelect}
      {...canvasProps}
    />
  )
}

export default UserSearchAutocomplete
